/**
 * The pack sounds, and the mute the player controls them with.
 *
 * A deliberately small thing. Slabz drives its audio through a ~1700-line
 * service with buses, tiers, ducking and per-category persistence; this screen
 * plays about eight clips and one loop, so porting that would be carrying an
 * architecture for a feature that does not have it yet. What IS ported is the
 * behaviour that matters: preloaded clips, a music bus separate from effects,
 * a remembered mute, and never throwing when the browser refuses to play.
 *
 * The clips themselves come from Slabz -- the Freak Mode effects the reel uses,
 * and the rip/hold pair -- which is the same audio, not a re-recording.
 *
 * **Autoplay.** A browser will not start audio before the page has been
 * touched, and it rejects the promise rather than erroring. Every `play()` here
 * swallows that: a blocked sound is not a broken page, and the first real click
 * on the pack is itself the gesture that unlocks the rest.
 */

/** Every clip this screen can play, by the name the screen calls it.
 *
 *  Each URL is a LITERAL `new URL(..., import.meta.url)`. That is not style: a
 *  bundler resolves these at build time by static analysis, and it cannot see
 *  through a template built from a variable. The first version of this file did
 *  `new URL(\`../../../assets/sounds/${path}\`, import.meta.url)` and Parcel
 *  silently emitted no audio at all, baking a `file:///src/js/...` base into the
 *  bundle -- every clip resolved to a path that does not exist on the server.
 *  Written out one per line, each file is found, hashed and copied.
 */
const CLIPS = {
    // The hold-to-open gesture.
    hold: new URL('../../../assets/sounds/packz/hold-and-rip.mp3', import.meta.url).href,
    // The reel: one start, a loop while it runs, one landing.
    reelStart: new URL('../../../assets/sounds/packz/spinner_start.mp3', import.meta.url).href,
    reelLoop: new URL('../../../assets/sounds/packz/spinner.mp3', import.meta.url).href,
    reelEnd: new URL('../../../assets/sounds/packz/spinner_end.mp3', import.meta.url).href,
    // The Freak Mode effects, which is what was asked for on the slot itself.
    tick: new URL('../../../assets/sounds/freak/counting_tick_sfx.mp3', import.meta.url).href,
    hit: new URL('../../../assets/sounds/freak/freak-big-hit.mp3', import.meta.url).href,
    vault: new URL('../../../assets/sounds/freak/vault_sfx.mp3', import.meta.url).href,
    // The reveal.
    reveal: new URL('../../../assets/sounds/packz/card-reveal-fanfare.mp3', import.meta.url).href,
    revealBlur: new URL('../../../assets/sounds/packz/reveal_card_blur.mp3', import.meta.url).href,
} as const

export type ClipId = keyof typeof CLIPS

/** The music bed.
 *
 *  Its own slot rather than a clip: it is the one sound with a lifetime longer
 *  than the gesture that started it, it loops, and it fades rather than starting
 *  and stopping flat.
 *
 *  **Slabz's rule, followed here.** Their arena suppresses the lobby loop for
 *  the whole of a rip -- purchasing, the reveal overlay, and any transition
 *  between -- with the comment that it stops "three looped tracks stacking on
 *  top of each other". The reel and the reveal have their own sound design and
 *  the bed underneath only muddies them, so this stops on open and comes back
 *  when the player is returned to the pack.
 */
const MUSIC = new URL('../../../assets/sounds/br1/main-theme-v2.wav', import.meta.url).href

/** Fade lengths. In is slower than out on purpose: arriving should be gentle,
 *  and leaving should be out of the way before the reel's first tick lands. */
const FADE_IN_MS = 1200
const FADE_OUT_MS = 450

let music: HTMLAudioElement | null = null
let fade: ReturnType<typeof setInterval> | null = null
let musicVolume = 0.25

/** Ramp the bed to a target volume, then optionally pause it.
 *
 *  A plain interval rather than the Web Audio API: this is one element and one
 *  linear ramp, and routing the bed through an AudioContext would mean owning a
 *  context, its autoplay-unlock lifecycle and a gain node for the sake of a
 *  fade that `volume` can already express.
 */
const rampTo = (target: number, ms: number, thenPause = false) => {
    if (!music) return
    if (fade) clearInterval(fade)
    const el = music
    const from = el.volume
    const steps = Math.max(1, Math.round(ms / 40))
    let i = 0
    fade = setInterval(() => {
        i += 1
        const v = from + (target - from) * (i / steps)
        try {
            el.volume = Math.min(1, Math.max(0, v))
        } catch {
            /* ignore */
        }
        if (i >= steps) {
            if (fade) clearInterval(fade)
            fade = null
            if (thenPause) {
                try {
                    el.pause()
                } catch {
                    /* ignore */
                }
            }
        }
    }, 40)
}

/** Start the bed, fading in. Safe to call repeatedly: if it is already playing
 *  this only brings the volume back up, which is what makes it correct to call
 *  from an effect that also runs on every re-render. */
export const playMusic = (volume = musicVolume) => {
    if (typeof Audio === 'undefined') return
    musicVolume = volume
    if (!music) {
        try {
            music = new Audio(MUSIC)
            music.loop = true
            music.preload = 'auto'
        } catch {
            music = null
            return
        }
    }
    if (muted) return
    try {
        // From wherever it is, not from zero: a bed that was ducked and is
        // coming back should not restart its fade from silence.
        if (music.paused) {
            music.volume = 0
            void music.play().catch(() => {})
        }
        rampTo(volume, FADE_IN_MS)
    } catch {
        /* ignore */
    }
}

/** Fade the bed out and pause it. Keeps the element and its position, so coming
 *  back resumes the track rather than restarting it. */
export const stopMusic = () => {
    if (!music || music.paused) return
    rampTo(0, FADE_OUT_MS, true)
}

const STORAGE_KEY = 'br1-packs-muted'

let muted = false
try {
    muted = localStorage.getItem(STORAGE_KEY) === '1'
} catch {
    // A blocked localStorage is not a reason to fail to make a sound.
}

const cache = new Map<ClipId, HTMLAudioElement>()

const clip = (id: ClipId): HTMLAudioElement | null => {
    if (typeof Audio === 'undefined') return null
    let a = cache.get(id)
    if (!a) {
        try {
            a = new Audio(CLIPS[id])
            a.preload = 'auto'
            cache.set(id, a)
        } catch {
            return null
        }
    }
    return a
}

/** Play one effect. Restarts it if it is already going, because these are short
 *  and a retrigger should be heard rather than swallowed. */
export const play = (id: ClipId, volume = 0.6) => {
    if (muted) return
    const a = clip(id)
    if (!a) return
    try {
        a.currentTime = 0
        a.volume = volume
        void a.play().catch(() => {})
    } catch {
        // Ignore. See the autoplay note at the top.
    }
}

/** Start the reel loop. Idempotent: calling it twice does not layer two copies. */
export const loop = (id: ClipId, volume = 0.4) => {
    const a = clip(id)
    if (!a) return
    a.loop = true
    if (muted) return
    try {
        a.volume = volume
        if (a.paused) void a.play().catch(() => {})
    } catch {
        /* ignore */
    }
}

export const stop = (id: ClipId) => {
    const a = cache.get(id)
    if (!a) return
    try {
        a.pause()
        a.currentTime = 0
    } catch {
        /* ignore */
    }
}

export const isMuted = () => muted

/** Mute or unmute everything, and remember it. Stops the music immediately
 *  rather than waiting for the next call, because a mute that leaves a loop
 *  running is the one bug nobody forgives. */
export const setMuted = (next: boolean) => {
    muted = next
    try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
    } catch {
        /* ignore */
    }
    if (next) {
        stopMusic()
        cache.forEach((a) => {
            try {
                a.pause()
            } catch {
                /* ignore */
            }
        })
    } else {
        playMusic()
    }
}
